import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, TrendingUp, DollarSign, Users, Target, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const InvestorsPage = () => {
  const investmentFocus = [
    {
      icon: Building2,
      title: 'Underperforming Regional Hotels',
      description: 'Properties with strong fundamentals but operational challenges that can be addressed through strategic management.'
    },
    {
      icon: Target,
      title: 'Emerging Metropolitan Assets',
      description: 'Apartment hotels in growing urban markets with untapped potential for corporate and leisure segments.'
    },
    {
      icon: TrendingUp,
      title: 'Franchise Optimization',
      description: 'Branded assets requiring enhanced operational performance and compliance excellence.'
    },
    {
      icon: Users,
      title: 'Repositioning Opportunities',
      description: 'Properties with clear upside potential through strategic repositioning and market reorientation.'
    }
  ];

  const partnershipModels = [
    {
      title: 'Co-Investment',
      description: 'Joint venture structures where we invest alongside partners, aligning interests and sharing in the upside.',
      features: ['Shared equity participation', 'Aligned interests based on investment share', 'Operational control', 'Strategic input']
    },
    {
      title: 'Management Agreements',
      description: 'Professional management services with performance-based incentives and transparent reporting.',
      features: ['Base fee: 4-7% of revenue', 'Performance incentives', 'Full operational control', 'Detailed reporting']
    },
    {
      title: 'Equity Partnerships',
      description: 'Direct equity investments in attractive opportunities that meet our investment criteria.',
      features: ['Direct ownership stake', 'Long-term value creation', 'Strategic oversight', 'Exit planning']
    },
    {
      title: 'Strategic Advisory',
      description: 'Consulting services for acquisition due diligence, repositioning, and performance improvement.',
      features: ['Project-based engagement', 'Specific deliverables', 'Expert guidance', 'Knowledge transfer']
    }
  ];

  const investmentCriteria = [
    'EBITDA growth potential through operational improvement',
    'Clear competitive positioning opportunity',
    'Favorable market fundamentals and demand drivers',
    'Reasonable capital expenditure requirements',
    'Strong asset quality and location',
    'Alignment with our operational expertise'
  ];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative h-[70vh] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1561501900-3701fa6a0864)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
        </div>
        
        <div className="relative h-full flex flex-col items-center justify-center text-white px-8 text-center pt-24">
          <h1 className="font-playfair text-5xl md:text-7xl font-bold mb-6 tracking-wide">
            For Investors
          </h1>
          <p className="text-xl md:text-2xl font-light max-w-3xl">
            Strategic partnerships focused on sustainable returns and long-term value creation
          </p>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-32 px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#2B2B2B] mb-6">
              Why Partner With Us
            </h2>
            <p className="text-xl text-[#3A2E24] max-w-3xl mx-auto">
              We bring operational excellence, financial discipline, and proven results to every investment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <Card className="p-8 text-center hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-20 h-20 bg-[#B89C4A] rounded-full flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="w-10 h-10 text-white" />
              </div>
              <h3 className="font-playfair text-2xl font-bold text-[#2B2B2B] mb-4">
                Proven Track Record
              </h3>
              <p className="text-[#3A2E24] leading-relaxed">
                Multiple successful turnarounds with measurable results: 5-star ratings, #1 rankings, and award nominations.
              </p>
            </Card>

            <Card className="p-8 text-center hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-20 h-20 bg-[#B89C4A] rounded-full flex items-center justify-center mx-auto mb-6">
                <DollarSign className="w-10 h-10 text-white" />
              </div>
              <h3 className="font-playfair text-2xl font-bold text-[#2B2B2B] mb-4">
                Financial Discipline
              </h3>
              <p className="text-[#3A2E24] leading-relaxed">
                Deep expertise in P&L management, budgeting, forecasting, and capital planning with institutional-grade reporting.
              </p>
            </Card>

            <Card className="p-8 text-center hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-20 h-20 bg-[#B89C4A] rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-10 h-10 text-white" />
              </div>
              <h3 className="font-playfair text-2xl font-bold text-[#2B2B2B] mb-4">
                Owner-Operator Mindset
              </h3>
              <p className="text-[#3A2E24] leading-relaxed">
                We operate as if we own it—because we understand ownership risk and are invested in long-term success.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Investment Focus */}
      <section className="py-32 px-8 bg-[#F4F4F4]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#2B2B2B] mb-6">
              Investment Focus
            </h2>
            <p className="text-xl text-[#3A2E24] max-w-3xl mx-auto">
              We target specific asset types where our expertise delivers maximum value.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {investmentFocus.map((focus, index) => {
              const Icon = focus.icon;
              return (
                <Card key={index} className="p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 bg-[#B89C4A] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="font-playfair text-2xl font-bold text-[#2B2B2B] mb-3">
                        {focus.title}
                      </h3>
                      <p className="text-[#3A2E24] leading-relaxed">
                        {focus.description}
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partnership Models */}
      <section className="py-32 px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#2B2B2B] mb-6">
              Partnership Structures
            </h2>
            <p className="text-xl text-[#3A2E24] max-w-3xl mx-auto">
              Flexible partnership models tailored to your investment objectives and risk profile.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {partnershipModels.map((model, index) => (
              <Card key={index} className="p-8 hover:shadow-2xl transition-all duration-300 border-t-4 border-t-[#B89C4A]">
                <h3 className="font-playfair text-2xl font-bold text-[#2B2B2B] mb-4">
                  {model.title}
                </h3>
                <p className="text-[#3A2E24] leading-relaxed mb-6">
                  {model.description}
                </p>
                <ul className="space-y-3">
                  {model.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-[#B89C4A] rounded-full flex-shrink-0" />
                      <span className="text-[#3A2E24]">{feature}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Criteria */}
      <section className="py-32 px-8 bg-[#2B2B2B] text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-playfair text-4xl md:text-5xl font-bold mb-8">
                Investment Criteria
              </h2>
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                We seek opportunities that align with our operational expertise and offer clear paths to value creation.
              </p>
              <ul className="space-y-4">
                {investmentCriteria.map((criterion, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#B89C4A] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-lg text-gray-200">{criterion}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1668480441891-3744c25337a3" 
                alt="Investment Opportunities"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Value Creation */}
      <section className="py-32 px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#2B2B2B] mb-6">
              Our Value Creation Model
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-[#B89C4A]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl font-bold text-[#B89C4A] font-playfair">1</span>
              </div>
              <h3 className="font-playfair text-xl font-bold text-[#2B2B2B] mb-4">
                Strategic Acquisition
              </h3>
              <p className="text-[#3A2E24]">
                Identify undervalued assets with clear improvement opportunities
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-[#B89C4A]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl font-bold text-[#B89C4A] font-playfair">2</span>
              </div>
              <h3 className="font-playfair text-xl font-bold text-[#2B2B2B] mb-4">
                Operational Enhancement
              </h3>
              <p className="text-[#3A2E24]">
                Implement proven strategies to drive revenue growth and cost optimization
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-[#B89C4A]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl font-bold text-[#B89C4A] font-playfair">3</span>
              </div>
              <h3 className="font-playfair text-xl font-bold text-[#2B2B2B] mb-4">
                Value Realization
              </h3>
              <p className="text-[#3A2E24]">
                Deliver sustainable cash flow growth and asset appreciation
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Documents */}
      <section className="py-32 px-8 bg-[#F4F4F4]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#2B2B2B] mb-8">
            Investment Documents
          </h2>
          <p className="text-xl text-[#3A2E24] mb-12">
            Download our comprehensive investment materials to learn more about partnership opportunities.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <a href="/documents/capability-statement.html" target="_blank" rel="noopener noreferrer">
              <Card className="p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer h-full">
                <FileText className="w-12 h-12 text-[#B89C4A] mx-auto mb-4" />
                <h3 className="font-semibold text-[#2B2B2B] mb-2">Capability Statement</h3>
                <p className="text-sm text-[#3A2E24] mb-4">Complete overview of our services and track record</p>
                <Button size="sm" variant="outline" className="w-full">Download</Button>
              </Card>
            </a>
            <a href="/documents/investment-overview.html" target="_blank" rel="noopener noreferrer">
              <Card className="p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer h-full">
                <FileText className="w-12 h-12 text-[#B89C4A] mx-auto mb-4" />
                <h3 className="font-semibold text-[#2B2B2B] mb-2">Investment Overview</h3>
                <p className="text-sm text-[#3A2E24] mb-4">Detailed investment approach and criteria</p>
                <Button size="sm" variant="outline" className="w-full">Download</Button>
              </Card>
            </a>
            <a href="/documents/franchise-buyer-guide.html" target="_blank" rel="noopener noreferrer">
              <Card className="p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer h-full">
                <FileText className="w-12 h-12 text-[#B89C4A] mx-auto mb-4" />
                <h3 className="font-semibold text-[#2B2B2B] mb-2">Franchise Buyer Guide</h3>
                <p className="text-sm text-[#3A2E24] mb-4">Essential information for franchise buyers</p>
                <Button size="sm" variant="outline" className="w-full">Download</Button>
              </Card>
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#2B2B2B] mb-8">
            Let's Explore Opportunities Together
          </h2>
          <p className="text-xl text-[#3A2E24] leading-relaxed mb-12">
            Schedule a confidential discussion to explore how we can partner for mutual success.
          </p>
          <Button 
            size="lg" 
            className="bg-[#B89C4A] hover:bg-[#A58B3A] text-white px-12 py-6 text-xl transition-all duration-300 hover:scale-105"
            asChild
          >
            <Link to="/contact">Schedule a Consultation <ArrowRight className="ml-2" /></Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default InvestorsPage;
